import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Briefcase, Trash2, Calendar, AlertCircle, Building, Users, Clock, History } from "lucide-react";

export default function OrdersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", order_code: "", client_name: "", start_date: "", expire: "" });
  const [empMap, setEmpMap] = useState({}); // order_id -> employees array
  const [empDialog, setEmpDialog] = useState(null); // selected order for dialog
  const [extendOrder, setExtendOrder] = useState(null); // {id, name, expire}
  const [extendForm, setExtendForm] = useState({ new_expire: "", reason: "" });

  const load = async () => {
    const r = await api.get("/orders");
    setItems(r.data);
    // Carica dipendenti per ogni commessa (in parallelo)
    const map = {};
    await Promise.all(
      r.data.map(async (o) => {
        try {
          const e = await api.get(`/orders/${o.id}/employees`);
          map[o.id] = e.data;
        } catch {
          map[o.id] = [];
        }
      })
    );
    setEmpMap(map);
  };
  useEffect(() => { load(); }, []);

  const up = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      await api.post("/orders", {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        expire: form.expire ? new Date(form.expire).toISOString() : null,
      });
      toast.success("Commessa creata");
      setForm({ name: "", order_code: "", client_name: "", start_date: "", expire: "" }); setOpen(false); load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare la commessa?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Eliminata"); load();
  };

  const openExtend = (order) => {
    // Pre-popola con +30 giorni dalla scadenza attuale (o oggi se senza scadenza)
    const base = order.expire ? new Date(order.expire) : new Date();
    base.setDate(base.getDate() + 30);
    setExtendForm({ new_expire: base.toISOString().slice(0, 10), reason: "" });
    setExtendOrder(order);
  };

  const saveExtend = async () => {
    if (!extendOrder || !extendForm.new_expire) {
      toast.error("Inserisci la nuova data di scadenza"); return;
    }
    try {
      await api.patch(`/orders/${extendOrder.id}/extend`, {
        new_expire: new Date(extendForm.new_expire).toISOString(),
        reason: extendForm.reason,
      });
      toast.success("Commessa prorogata");
      setExtendOrder(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore proroga");
    }
  };

  const isExpired = (d) => d && new Date(d) < new Date();

  return (
    <div className="p-4 md:p-12" data-testid="orders-page">
      <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Commesse · Clienti · Progetti</div>
          <h1 className="font-display text-4xl">Commesse / Cantieri</h1>
          <p className="text-[#555] mt-1 text-sm">Gestione progetti con cliente, scadenza, dipendenti assegnati</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-order-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11 btn-glow">
              <Plus className="w-4 h-4 mr-2"/> Nuova commessa
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl border-0 max-w-md">
            <DialogHeader><DialogTitle className="font-display text-2xl">Nuova commessa</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome / Descrizione *</Label><Input data-testid="order-name" required className="rounded-xl mt-1" value={form.name} onChange={up("name")}/></div>
              <div><Label>Codice commessa *</Label><Input data-testid="order-code" required className="rounded-xl mt-1 uppercase" value={form.order_code} onChange={(e)=>setForm({...form, order_code: e.target.value.toUpperCase()})} placeholder="C-2026-001"/></div>
              <div><Label>Cliente / Committente</Label><Input data-testid="order-client" className="rounded-xl mt-1" value={form.client_name} onChange={up("client_name")} placeholder="Es. Rossi S.p.A."/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Data inizio</Label><Input type="date" data-testid="order-start-date" className="rounded-xl mt-1" value={form.start_date} onChange={up("start_date")}/></div>
                <div><Label>Scadenza</Label><Input type="date" data-testid="order-expire" className="rounded-xl mt-1" value={form.expire} onChange={up("expire")}/></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="order-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.length === 0 && <div className="col-span-full card-smart p-12 text-center text-[#757575]"><Briefcase className="w-10 h-10 mx-auto mb-3 text-[#FF7A29]/50"/>Nessuna commessa attiva.</div>}
        {items.map((it) => {
          const emps = empMap[it.id] || [];
          return (
            <div key={it.id} className="card-smart p-6 flex flex-col" data-testid={`order-${it.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#FFF0E3] text-[#FF7A29] flex items-center justify-center"><Briefcase className="w-5 h-5"/></div>
                <Button size="icon" variant="ghost" onClick={()=>del(it.id)} className="hover:bg-red-50 hover:text-red-600 rounded-full h-8 w-8"><Trash2 className="w-3.5 h-3.5"/></Button>
              </div>
              <h3 className="font-display text-xl mb-1">{it.name}</h3>
              <div className="text-xs font-mono text-[#FF7A29] mb-2">{it.order_code}</div>
              {it.client_name && (
                <div className="text-xs text-[#444] flex items-center gap-1 mb-3" data-testid={`order-client-${it.id}`}>
                  <Building className="w-3 h-3"/> {it.client_name}
                </div>
              )}

              {/* Dipendenti assegnati */}
              <div className="mt-1 mb-3">
                {emps.length === 0 ? (
                  <div className="text-[11px] text-[#999] italic">Nessun dipendente assegnato</div>
                ) : (
                  <button
                    type="button"
                    onClick={()=>setEmpDialog(it)}
                    data-testid={`order-emp-chip-${it.id}`}
                    className="inline-flex items-center gap-1 text-[11px] bg-[#FFF0E3] text-[#E85D00] hover:bg-[#FF7A29] hover:text-white transition-colors px-2.5 py-1 rounded-full font-semibold"
                  >
                    <Users className="w-3 h-3"/> {emps.length} dipendent{emps.length === 1 ? "e" : "i"}
                  </button>
                )}
              </div>

              <div className="mt-auto pt-3 border-t border-[#EDE5D8] space-y-1.5 text-xs">
                {it.start_date && (
                  <div className="text-[#757575] flex items-center gap-1" data-testid={`order-start-${it.id}`}>
                    <Calendar className="w-3 h-3"/> Inizio {new Date(it.start_date).toLocaleDateString("it-IT")}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3"/>
                  {it.expire ? (
                    <span className={isExpired(it.expire) ? "text-red-600 font-semibold flex items-center gap-1" : "text-[#757575]"}>
                      {isExpired(it.expire) && <AlertCircle className="w-3 h-3"/>}
                      Scade {new Date(it.expire).toLocaleDateString("it-IT")}
                    </span>
                  ) : <span className="text-[#757575]">Senza scadenza</span>}
                </div>
                {it.extensions && it.extensions.length > 0 && (
                  <div className="text-[#FF7A29] flex items-center gap-1 font-medium" data-testid={`order-ext-count-${it.id}`}>
                    <History className="w-3 h-3"/> {it.extensions.length} {it.extensions.length === 1 ? "proroga" : "proroghe"}
                  </div>
                )}
                <Button
                  onClick={() => openExtend(it)}
                  data-testid={`order-extend-${it.id}`}
                  variant="outline"
                  size="sm"
                  className="w-full mt-2 rounded-full border-[#FF7A29] text-[#E85D00] hover:bg-[#FFF0E3] h-8 text-xs gap-1"
                >
                  <Clock className="w-3 h-3"/> Proroga data
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dialog dipendenti assegnati */}
      <Dialog open={!!empDialog} onOpenChange={(v)=>{ if (!v) setEmpDialog(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-lg" data-testid="order-employees-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Dipendenti collocati</DialogTitle>
          </DialogHeader>
          {empDialog && (
            <div className="space-y-3">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{empDialog.name}</div>
                <div className="text-xs text-[#757575] font-mono">{empDialog.order_code}{empDialog.client_name ? " · " + empDialog.client_name : ""}</div>
              </div>
              <div className="space-y-2 max-h-96 overflow-auto">
                {(empMap[empDialog.id] || []).map((e) => (
                  <div key={e.id} className="flex items-center justify-between bg-white border border-[#EDE5D8] rounded-xl px-3 py-2">
                    <div>
                      <div className="font-semibold text-sm">{e.name} {e.lastname}</div>
                      <div className="text-xs text-[#757575]">{e.job || "—"} · {e.email}</div>
                    </div>
                    <div className="text-[10px] font-mono text-[#FF7A29]">{e.fiscal_code}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog proroga commessa */}
      <Dialog open={!!extendOrder} onOpenChange={(v)=>{ if (!v) setExtendOrder(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="order-extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Clock className="w-6 h-6 text-[#FF7A29]"/> Proroga commessa
            </DialogTitle>
          </DialogHeader>
          {extendOrder && (
            <div className="space-y-3">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{extendOrder.name}</div>
                <div className="text-xs text-[#757575] font-mono">{extendOrder.order_code}</div>
                <div className="text-xs text-[#444] mt-2">
                  <strong>Scadenza attuale:</strong> {extendOrder.expire ? new Date(extendOrder.expire).toLocaleDateString("it-IT") : "—"}
                </div>
              </div>
              <div>
                <Label>Nuova data di scadenza *</Label>
                <Input
                  type="date"
                  data-testid="order-extend-date"
                  className="rounded-xl mt-1"
                  value={extendForm.new_expire}
                  onChange={(e)=>setExtendForm({...extendForm, new_expire: e.target.value})}
                  min={new Date().toISOString().slice(0, 10)}
                />
              </div>
              <div>
                <Label>Motivo / Note <span className="text-[#999]">(facoltativo)</span></Label>
                <Input
                  data-testid="order-extend-reason"
                  className="rounded-xl mt-1"
                  value={extendForm.reason}
                  onChange={(e)=>setExtendForm({...extendForm, reason: e.target.value})}
                  placeholder="Es. Estensione lavori richiesta dal committente"
                  maxLength={200}
                />
              </div>
              {extendOrder.extensions && extendOrder.extensions.length > 0 && (
                <div className="rounded-xl border border-[#EDE5D8] bg-white p-3" data-testid="order-extend-history">
                  <div className="text-[10px] uppercase tracking-widest text-[#999] font-bold mb-2 flex items-center gap-1">
                    <History className="w-3 h-3"/> Storico proroghe ({extendOrder.extensions.length})
                  </div>
                  <div className="space-y-1.5 max-h-32 overflow-auto text-xs">
                    {extendOrder.extensions.slice().reverse().map((ext, i) => (
                      <div key={i} className="text-[#555] border-l-2 border-[#FF7A29]/40 pl-2">
                        <span className="font-mono">{ext.from ? new Date(ext.from).toLocaleDateString("it-IT") : "—"}</span>
                        {" → "}
                        <span className="font-mono font-semibold text-[#E85D00]">{new Date(ext.to).toLocaleDateString("it-IT")}</span>
                        {ext.reason && <div className="text-[#888] italic">"{ext.reason}"</div>}
                        <div className="text-[10px] text-[#999]">da {ext.by_user_name} il {new Date(ext.at).toLocaleDateString("it-IT")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={()=>setExtendOrder(null)} variant="ghost" className="rounded-full">Annulla</Button>
            <Button onClick={saveExtend} data-testid="order-extend-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">
              <Clock className="w-4 h-4 mr-1.5"/> Conferma proroga
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
